// Product cards in articles and pages. Placeholders (.article-product-embed[data-product-handle]) are replaced by
// the article-product-embed section. On top of that:
// - a sold-out game gets an in-stock alternative from Shopify's related recommendations,
// - "Lägg i varukorg" adds to the cart drawer without leaving the article (falls back to a normal form post),
// - a "Spelen i guiden" strip after the introduction lists every game with price and stock, in-stock games first.
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // A preorder carries the same line item properties as the theme's other buy buttons.
  const addForm = (variantId, label, extraClass = '', preorder = null) => `
    <form method="post" action="${root}cart/add" class="article-product-card__form ${extraClass}" data-article-product-form>
      <input type="hidden" name="id" value="${escapeHtml(variantId)}">
      <input type="hidden" name="quantity" value="1">
      ${preorder ? '<input type="hidden" name="properties[Typ]" value="Förbeställning">' : ''}
      ${preorder && preorder.eta ? `<input type="hidden" name="properties[_Leverans]" value="${escapeHtml(preorder.eta)}">` : ''}
      <button type="submit" class="article-product-card__button">${escapeHtml(label)}</button>
    </form>`;

  // Stock state from the card (sections/article-product-embed.liquid): in_stock only when tracked inventory is
  // above 0; preorder when it is 0 or less and the product continues selling; untracked; sold_out.
  const stockOf = (card) => card.dataset.stock || 'sold_out';
  const STOCK_LABEL = { in_stock: 'I lager', preorder: 'Förbeställning', untracked: 'Kan beställas', sold_out: 'Slut just nu' };
  const STOCK_ORDER = { in_stock: 0, preorder: 1, untracked: 1, sold_out: 2 };

  // The product card rendered by the theme, with the current stock (not cached in the article).
  async function fetchCard(handle) {
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=article-product-embed`);
    if (!response.ok) throw new Error(`Could not load product: ${handle}`);
    const template = document.createElement('template');
    template.innerHTML = (await response.text()).trim();
    return template.content.querySelector('.article-product-card');
  }

  async function loadCard(placeholder) {
    const handle = placeholder.dataset.productHandle;
    if (!handle) return null;
    try {
      const card = await fetchCard(handle);
      if (!card) return null;
      placeholder.replaceWith(card);
      return card;
    } catch (error) {
      console.error('Article product embed error:', error);
      return null;
    }
  }

  // Accessories, bundles and gift cards are no stand-in for a game.
  const NOT_A_GAME = /sleeve|tillbeh|paket|presentkort|gift card|kortskydd|playmat|tärning|dice|insert|organizer/i;
  const isGame = (p) => !NOT_A_GAME.test(`${p.title} ${p.type || ''} ${(p.tags || []).join(' ')}`);
  const productsFrom = async (url) => {
    try {
      const response = await fetch(url);
      return response.ok ? (await response.json()).products || [] : [];
    } catch {
      return [];
    }
  };
  // /collections/<handle>/products.json has another shape than the recommendations: prices as "499.00", no
  // product-level availability, product_type, images. Bring it to the recommendations shape (prices in öre).
  const cents = (value) => Math.round(parseFloat(value) * 100);
  const fromCollection = (p) => ({
    handle: p.handle,
    title: p.title,
    type: p.product_type,
    tags: p.tags,
    url: `${root}products/${p.handle}`,
    featured_image: p.images && p.images[0] ? p.images[0].src : '',
    available: p.variants.some((v) => v.available),
    price: cents(p.variants[0].price),
    variants: p.variants.map((v) => ({ id: v.id, available: v.available, price: cents(v.price) })),
  });

  // Sold out: suggest a game that is in stock now, in about the same price class and not already featured in
  // the article. Shopify's related recommendations first, then the shop's best sellers. Their JSON calls a
  // preorder "available", so each candidate's own card decides whether it really is in stock.
  async function addAlternative(card, featured) {
    if (card.dataset.available === 'true' || !card.dataset.productId) return;
    try {
      const price = Number(card.dataset.priceCents) || 0;
      const suitable = (p) => p.available && !featured.has(p.handle) && isGame(p) && (!price || (p.price >= price * 0.4 && p.price <= price * 2));
      const candidates = [
        ...(await productsFrom(`${root}recommendations/products.json?product_id=${card.dataset.productId}&limit=10&intent=related`)).filter(suitable),
        ...(await productsFrom(`${root}collections/toppaaljare/products.json?limit=30`)).map(fromCollection).filter(suitable),
      ];
      let alternative = null;
      for (const candidate of candidates.slice(0, 6)) {
        if (featured.has(candidate.handle)) continue;
        const candidateCard = await fetchCard(candidate.handle).catch(() => null);
        if (candidateCard && stockOf(candidateCard) === 'in_stock') {
          alternative = { ...candidate, card: candidateCard.dataset };
          break;
        }
      }
      if (!alternative) return;
      featured.add(alternative.handle); // different sold-out games get different suggestions
      const d = alternative.card;
      const image = alternative.featured_image ? `${alternative.featured_image}${alternative.featured_image.includes('?') ? '&' : '?'}width=160` : '';
      const box = document.createElement('div');
      box.className = 'article-product-card__alternative';
      box.innerHTML = `
        <p class="article-product-card__alternative-label">Slut just nu – prova i stället</p>
        <div class="article-product-card__alternative-row">
          ${image ? `<a href="${escapeHtml(d.url)}" class="article-product-card__alternative-image"><img src="${escapeHtml(image)}" alt="${escapeHtml(d.title)}" loading="lazy" width="80" height="80"></a>` : ''}
          <div class="article-product-card__alternative-info">
            <a href="${escapeHtml(d.url)}" class="article-product-card__alternative-title">${escapeHtml(d.title)}</a>
            <span class="article-product-card__alternative-price">${escapeHtml(d.price)} · I lager</span>
          </div>
        </div>
        ${addForm(d.variantId, 'Lägg i varukorg', 'article-product-card__form--alternative')}`;
      card.querySelector('.article-product-card__button--disabled')?.remove();
      card.querySelector('.article-product-card__info').appendChild(box);
    } catch (error) {
      console.error('Article alternative error:', error);
    }
  }

  // Add to cart without leaving the article: the theme's cart drawer, or a normal form post as a fallback.
  async function addToCart(form) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') {
      form.submit();
      return;
    }
    const button = form.querySelector('button');
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Lägger i varukorgen…';
    try {
      const body = new FormData(form);
      body.append('sections', 'cart-drawer,cart-icon-bubble');
      body.append('sections_url', window.location.pathname);
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body });
      const state = await response.json();
      if (state.status) throw new Error(state.description || state.message || 'Kunde inte lägga i varukorgen');
      drawer.classList.remove('is-empty');
      drawer.renderContents(state);
      button.textContent = 'Tillagd ✓';
      setTimeout(() => (button.textContent = label), 2500);
    } catch (error) {
      button.textContent = label;
      window.alert(error.message);
    } finally {
      button.disabled = false;
    }
  }

  // "Spelen i guiden": every featured game in one row before the first heading, in-stock games first, then
  // preorders, then sold out.
  function buildStrip(cards) {
    if (cards.length < 3) return;
    const firstHeading = cards[0].closest('.article-template__content, .rte, .page-width')?.querySelector('h2');
    if (!firstHeading) return;
    const sorted = [...cards].sort((a, b) => STOCK_ORDER[stockOf(a)] - STOCK_ORDER[stockOf(b)]);
    const inStock = cards.filter((c) => stockOf(c) === 'in_stock').length;
    const strip = document.createElement('nav');
    strip.className = 'article-games';
    strip.setAttribute('aria-label', 'Spelen i guiden');
    strip.innerHTML = `
      <p class="article-games__heading">Spelen i guiden <span>${cards.length} spel · ${inStock} i lager</span></p>
      <ul class="article-games__list">
        ${sorted
          .map((c) => {
            const d = c.dataset;
            const stock = stockOf(c);
            const buyable = d.available === 'true' && stock !== 'sold_out';
            return `<li class="article-games__item article-games__item--${stock.replace('_', '-')}${stock === 'sold_out' ? ' article-games__item--soldout' : ''}">
              <a href="${escapeHtml(d.url)}" class="article-games__link">
                ${d.image ? `<img src="${escapeHtml(d.image)}" alt="" loading="lazy" width="96" height="96">` : ''}
                <span class="article-games__title">${escapeHtml(d.title)}</span>
                <span class="article-games__meta">${escapeHtml(d.price)} · ${STOCK_LABEL[stock]}</span>
              </a>
              ${buyable ? addForm(d.variantId, stock === 'preorder' ? 'Förbeställ' : 'Köp', 'article-games__form', stock === 'preorder' ? { eta: d.eta } : null) : ''}
            </li>`;
          })
          .join('')}
      </ul>`;
    firstHeading.before(strip);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-article-product-form]');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  });

  document.addEventListener('DOMContentLoaded', async () => {
    const placeholders = [...document.querySelectorAll('.article-product-embed[data-product-handle]')];
    if (!placeholders.length) return;
    const cards = (await Promise.all(placeholders.map(loadCard))).filter(Boolean);
    const featured = new Set(cards.map((c) => c.dataset.productHandle));
    buildStrip(cards);
    cards.forEach((card) => addAlternative(card, featured));
  });
})();
